import { useState, useEffect } from 'react'
import { Wrench, Package, CheckCircle, Clock, Euro, AlertTriangle, Settings, List, Truck, FileText } from 'lucide-react'
import RepairsListTab from '@/components/repairs/RepairsListTab'
import RepairsConfigTab from '@/components/repairs/RepairsConfigTab'
import RepairServicesTab from '@/components/repairs/RepairServicesTab'
import api from '@/lib/api'
import { formatPrice } from '@/lib/utils'

type TabType = 'all' | 'pending' | 'workshop' | 'quotes' | 'completed' | 'services' | 'config'

interface TabConfig {
  id: TabType
  label: string
  icon: React.ElementType
  statusFilter?: string
  description: string
}

interface Stats {
  pending: number
  in_workshop: number
  quotes_pending: number
  waiting_parts: number
  completed_month: number
  invoiced_month: number
  acceptance_rate: number
  avg_days: number
}

const TABS: TabConfig[] = [
  { id: 'all', label: 'Todas', icon: Package, description: 'Todas las reparaciones' },
  { id: 'pending', label: 'Pendientes', icon: Clock, statusFilter: 'requested,approved,pickup_scheduled,in_transit', description: 'Pendientes de recibir' },
  { id: 'workshop', label: 'En taller', icon: Wrench, statusFilter: 'received,diagnosing,quote_sent,quote_approved,waiting_parts,repairing,quality_check,not_repairable,payment_pending,payment_confirmed', description: 'En proceso de reparación' },
  { id: 'quotes', label: 'Presupuestos', icon: FileText, statusFilter: 'quote_sent', description: 'Presupuestos pendientes de respuesta' },
  { id: 'completed', label: 'Completadas', icon: CheckCircle, statusFilter: 'return_shipped,delivered,completed,quote_rejected', description: 'Reparaciones finalizadas' },
  { id: 'services', label: 'Servicios', icon: List, description: 'Catálogo de servicios del taller' },
  { id: 'config', label: 'Ajustes', icon: Settings, description: 'Configuración del módulo' },
]

export default function RepairsPage() {
  const [activeTab, setActiveTab] = useState<TabType>('all')
  const [stats, setStats] = useState<Stats | null>(null)

  useEffect(() => {
    loadStats()
  }, [])

  const loadStats = async () => {
    try {
      const response = await api.get('/repairs/admin/stats')
      setStats(response.data)
    } catch (error) {
      console.error('Error loading repair stats:', error)
    }
  }

  const getTabCount = (tabId: TabType): number | undefined => {
    if (!stats) return undefined
    switch (tabId) {
      case 'pending': return stats.pending
      case 'workshop': return stats.in_workshop
      case 'quotes': return stats.quotes_pending
      case 'completed': return stats.completed_month
      default: return undefined
    }
  }

  const currentTab = TABS.find(t => t.id === activeTab)

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10">
        <div className="px-6">
          <div className="py-4 border-b border-slate-100 dark:border-slate-700/50">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Reparaciones</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              Gestión de solicitudes de reparación, presupuestos y taller
            </p>

            {/* KPI Grid */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                <div className="flex items-center gap-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200/60 dark:border-amber-800/40 rounded-xl px-4 py-3">
                  <div className="p-2 bg-amber-100 dark:bg-amber-900/50 rounded-lg">
                    <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-700 dark:text-amber-300 leading-none">{stats.pending}</p>
                    <p className="text-xs text-amber-600/80 dark:text-amber-400/80 mt-0.5">Pendientes</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200/60 dark:border-blue-800/40 rounded-xl px-4 py-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
                    <Wrench className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-700 dark:text-blue-300 leading-none">{stats.in_workshop}</p>
                    <p className="text-xs text-blue-600/80 dark:text-blue-400/80 mt-0.5">En taller</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 bg-orange-50 dark:bg-orange-950/30 border border-orange-200/60 dark:border-orange-800/40 rounded-xl px-4 py-3">
                  <div className="p-2 bg-orange-100 dark:bg-orange-900/50 rounded-lg">
                    <FileText className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-orange-700 dark:text-orange-300 leading-none">{stats.quotes_pending}</p>
                    <p className="text-xs text-orange-600/80 dark:text-orange-400/80 mt-0.5">Presupuestos pend.</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200/60 dark:border-emerald-800/40 rounded-xl px-4 py-3">
                  <div className="p-2 bg-emerald-100 dark:bg-emerald-900/50 rounded-lg">
                    <Euro className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300 leading-none">{formatPrice(stats.invoiced_month)}</p>
                    <p className="text-xs text-emerald-600/80 dark:text-emerald-400/80 mt-0.5">Facturado mes</p>
                  </div>
                </div>
              </div>
            )}

            {/* Secondary KPIs */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-700/30 rounded-lg">
                  <Truck className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-xs text-slate-500 dark:text-slate-400">Esperando piezas: <strong className="text-slate-700 dark:text-slate-300">{stats.waiting_parts}</strong></span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-700/30 rounded-lg">
                  <CheckCircle className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-xs text-slate-500 dark:text-slate-400">Completadas mes: <strong className="text-slate-700 dark:text-slate-300">{stats.completed_month}</strong></span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-700/30 rounded-lg">
                  <FileText className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-xs text-slate-500 dark:text-slate-400">Tasa aceptación: <strong className="text-slate-700 dark:text-slate-300">{stats.acceptance_rate}%</strong></span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-700/30 rounded-lg">
                  <Clock className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-xs text-slate-500 dark:text-slate-400">Tiempo medio: <strong className="text-slate-700 dark:text-slate-300">{stats.avg_days}d</strong></span>
                </div>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 pt-2 overflow-x-auto scrollbar-hide">
            {TABS.map((tab) => {
              const Icon = tab.icon
              const isActive = activeTab === tab.id
              const count = getTabCount(tab.id)

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-3.5 py-2.5 border-b-2 transition-all whitespace-nowrap text-sm ${
                    isActive
                      ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400 font-semibold'
                      : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 font-medium'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-indigo-500' : ''}`} />
                  {tab.label}
                  {count !== undefined && count > 0 && (
                    <span className={`ml-0.5 px-1.5 py-0.5 text-[11px] font-semibold rounded-full leading-none ${
                      isActive
                        ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
                        : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto" style={{ height: 'calc(100vh - 300px)' }}>
        {activeTab === 'config' ? (
          <RepairsConfigTab />
        ) : activeTab === 'services' ? (
          <RepairServicesTab />
        ) : (
          <RepairsListTab
            statusFilter={currentTab?.statusFilter}
            onStatsChange={loadStats}
          />
        )}
      </div>
    </div>
  )
}
